import { Box, Stack, Tooltip, Typography } from '@mui/material';

/**
 * SkillX attendance register — GlobalTable headers, built per month.
 *
 * The shape follows HR's "All Attendance" screen: fixed columns on the left,
 * then one narrow column per day of the month. Unlike that screen the day
 * columns are built here rather than in a redux reducer, because the count
 * depends only on the month being viewed and nothing else needs to read them.
 *
 * EVERY renderCell READS `row`, NEVER `value`. GlobalTable's getCellValue
 * substitutes an em dash for any falsy cell before renderCell is called, so an
 * off day — whose code is the empty string — would arrive as "—" and read as
 * missing data rather than "no class that day".
 */

/** One colour per short code. Matches the learner's calendar. */
export const CODE_META = {
  P: { label: 'Present', color: '#1E7E4F', bg: 'rgba(30,126,79,0.12)' },
  H: { label: 'Half marked', color: '#C25E00', bg: 'rgba(194,94,0,0.12)' },
  N: { label: 'Not marked', color: '#C0392B', bg: 'rgba(192,57,43,0.10)' },
  L: { label: 'Approved leave', color: '#7C4E9F', bg: 'rgba(124,78,159,0.12)' },
  '?': { label: 'Not recorded', color: '#66708A', bg: 'rgba(102,112,138,0.10)' },
  R: { label: 'Rescheduled', color: '#2E75BC', bg: 'rgba(46,117,188,0.12)' },
  HO: { label: 'Holiday', color: '#14A0DA', bg: 'rgba(20,160,218,0.12)' },
};

/** What a day cell says when you hover it. */
const dayTooltip = (detail, dayNo) => {
  if (!detail) return '';
  const meta = CODE_META[detail.code];
  const bits = [`Day ${dayNo} — ${meta ? meta.label : 'No class'}`];
  if (detail.checkIn || detail.checkOut) {
    bits.push(`In ${detail.checkIn || '--:--'} · Out ${detail.checkOut || '--:--'}`);
  }
  if (detail.movedTo) bits.push(`Moved to ${detail.movedTo}`);
  if (detail.verified === false) bits.push('Tutor marked, not yet verified');
  if (detail.note) bits.push(detail.note);
  return bits.join('\n');
};

const DayCell = ({ row, dayNo }) => {
  const code = row?.[String(dayNo)] || '';
  const detail = { ...(row?.dayDetail?.[dayNo] || {}), code };
  const meta = CODE_META[code];

  // No class that day. A faint dot, not a blank — an empty cell in a grid of
  // 31 reads as a rendering failure.
  if (!meta) {
    return (
      <Tooltip arrow title={dayTooltip(detail, dayNo)}>
        <Box sx={{ color: 'text.disabled', fontSize: 13, lineHeight: 1 }}>·</Box>
      </Tooltip>
    );
  }

  const hasTimes = detail.checkIn || detail.checkOut;

  return (
    <Tooltip arrow title={dayTooltip(detail, dayNo)}>
      <Stack alignItems="center" spacing={0.25} sx={{ py: 0.4 }}>
        <Box
          sx={{
            minWidth: 30,
            px: 0.75,
            py: 0.25,
            borderRadius: 0.75,
            fontSize: 12,
            fontWeight: 800,
            color: meta.color,
            background: meta.bg,
            textAlign: 'center',
          }}
        >
          {code}
        </Box>

        {/* The scanner times, the way HR's All Attendance shows them. Only
            offline days carry any — an online batch has nothing to stamp. */}
        {hasTimes && (
          <Typography
            sx={{ fontSize: 9.5, color: 'text.secondary', lineHeight: 1.25, whiteSpace: 'nowrap' }}
          >
            {detail.checkIn || '--:--'}
            <br />
            {detail.checkOut || '--:--'}
          </Typography>
        )}

        {/* A move was asked for but has not happened: the class is still on. */}
        {detail.rescheduleRequested && (
          <Typography sx={{ fontSize: 9, color: '#14A0DA', lineHeight: 1.2 }}>req</Typography>
        )}
      </Stack>
    </Tooltip>
  );
};

/**
 * @param {number} daysInMonth
 */
export const buildSkillxAttendanceHead = (daysInMonth = 0) => {
  const dayColumns = Array.from({ length: daysInMonth }, (_, i) => {
    const dayNo = i + 1;

    return {
      field: String(dayNo),
      headerName: String(dayNo),
      width: 84,
      align: 'center',
      // `nullable` keeps GlobalTable from turning an off day's "" into an em
      // dash before the cell is drawn.
      nullable: true,
      renderCell: ({ row }) => <DayCell row={row} dayNo={dayNo} />,
    };
  });

  return [
    { field: 'sl_no', headerName: '#', width: 70 },
    {
      field: 'admn_no',
      headerName: 'Adm No',
      width: 120,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{row.admn_no}</Typography>
      ),
    },
    {
      field: 'student_name',
      headerName: 'Student',
      width: 220,
      align: 'left',
      renderCell: ({ row }) => (
        <Stack spacing={0.2} sx={{ py: 0.5 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>
            {row.student_name}
          </Typography>
          {/* Said on the row, because a whole row of "?" is otherwise a mystery */}
          {!row.tracked && (
            <Typography sx={{ fontSize: 11, color: '#C25E00', lineHeight: 1.3 }}>
              no attendance source
            </Typography>
          )}
          {!row.is_active && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
              course ended
            </Typography>
          )}
        </Stack>
      ),
    },
    ...dayColumns,
    // The month's counts, per learner. Widened so the headers are not clipped
    // — a column headed "Not m…" is a column nobody trusts.
    { field: 'present', headerName: 'Present (days)', width: 175 },
    { field: 'halfMarked', headerName: 'Half marked', width: 175 },
    { field: 'notMarked', headerName: 'Not marked', width: 175 },
    { field: 'absent', headerName: 'On leave', width: 140 },
    {
      field: 'held',
      headerName: 'Classes held',
      width: 160,
      toolTipGetter: 'Present + Half marked + Not marked. Leave, holidays and off days are excluded.',
    },
  ];
};
